/**
 * The sent-GIF bubble (R3).
 *
 * A GIF message carries one `gifObjectId`, which is a media-api object id
 * exactly like a photo's, because `/gif/select` re-hosts the provider's
 * bytes as our own object (Q201) before `/message/send` ever sees it. So
 * this resolves it through `/object/get-batch` rather than a client-derived
 * path, the same rule `MultiPhotoBubble` follows, and keeps the same three
 * states apart: a skeleton in flight, the GIF once it resolves to
 * `published`, and the neutral unavailable placeholder for anything else.
 *
 * The provider attribution mark rides on the sent bubble as well as on the
 * picker tile. Re-hosting our own copy does not lift the provider's
 * attribution requirement (the brief says so in as many words), and the
 * bubble is where the GIF is actually seen.
 */
import { useObjectGetBatch } from "@sportsmet/media-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  GIF_ATTRIBUTION_MARK,
  PHOTO_UNAVAILABLE_LABEL,
} from "./chatExtrasCopy";

export type typeGifBubbleProps = {
  gifObjectId: string;
};

export const GifBubble = (props: typeGifBubbleProps): ReactNode => {
  const query = useObjectGetBatch({ mediaIds: [props.gifObjectId] });
  const item = (query.data?.data?.items ?? []).find(
    (candidate) => candidate.mediaId === props.gifObjectId,
  );
  const isBatchFailed = query.isError || query.data?.error != null;

  if (query.isPending) {
    return (
      <div data-testid="gif-bubble-loading" className="w-56">
        <Skeleton shape="card" className="aspect-video w-full" />
      </div>
    );
  }

  if (isBatchFailed || item?.status !== "published" || !item.variants) {
    return (
      <div
        data-testid="gif-bubble-unavailable"
        className="flex aspect-video w-56 items-center justify-center rounded-md bg-surface-sunken text-caption text-ink-tertiary"
      >
        {PHOTO_UNAVAILABLE_LABEL}
      </div>
    );
  }

  return (
    <div
      data-testid="gif-bubble"
      className="relative w-56 overflow-hidden rounded-md bg-surface-sunken"
    >
      <img
        src={item.variants.large.url}
        alt=""
        className="w-full object-cover"
        data-testid="gif-bubble-image"
      />
      <span
        data-testid="gif-bubble-attribution"
        className="absolute right-0 bottom-0 bg-scrim px-1 text-caption text-on-primary"
      >
        {GIF_ATTRIBUTION_MARK}
      </span>
    </div>
  );
};
